<template>
    <div class="SimpleContextualInfo">
        <div class="row items-center">
            <QCard
                class="SimpleContextualInfoCompactCard"
                tabindex="0"
                role="button"
                @mouseenter="handleCompactCardMouseEnter"
                @mouseleave="handleCompactCardMouseLeave"
                @focusin="handleCompactCardMouseEnter"
                @focusout="handleCompactCardMouseLeave"
                @keypress.enter.prevent="toggleCompactMenu"
                @keypress.space.prevent="toggleCompactMenu"
            >
                <div class="SimpleContextualInfoCompactLabel row items-stretch no-wrap">
                    <div class="SimpleContextualInfoCompactLabelLeft column justify-center">
                        <div class="SimpleContextualInfoCompactValue text-primary">
                            <span class="text-weight-bold">
                                {{ compactCasesValue }}
                            </span>
                            <span>{{ ` ${$t('visualization.filters.contextualInfo.of')} ` }}</span>
                            <span class="text-weight-bold">
                                {{ compactCasesTotal }}
                            </span>
                        </div>
                        <div class="SimpleContextualInfoCompactCaption text-grey-8">
                            {{ $t('visualization.filters.contextualInfo.totalCases') }}
                        </div>
                    </div>

                    <div class="SimpleContextualInfoCompactLabelRight">
                        <div class="SimpleContextualInfoCompactPercentage text-white text-weight-medium">
                            {{ compactCasesPercentage }}%
                        </div>
                    </div>
                </div>

                <QMenu
                    v-model="compactMenuOpen"
                    anchor="bottom left"
                    self="top left"
                    :offset="[0, 6]"
                    :autoClose="false"
                >
                    <div
                        class="q-pa-sm"
                        @mouseenter="handleCompactMenuMouseEnter"
                        @mouseleave="handleCompactMenuMouseLeave"
                        @focusin="handleCompactMenuMouseEnter"
                        @focusout="handleCompactMenuMouseLeave"
                    >
                        <div class="row items-stretch q-col-gutter-sm">
                            <div class="col-3 SimpleContextualInfoMenuCol">
                                <div class="SimpleContextualInfoMenuTitle row items-center q-mb-xs">
                                    <q-icon name="storage" color="primary" size="18px" class="q-mr-xs" />
                                    <div class="text-caption text-primary text-weight-bold">
                                        {{ $t('visualization.filters.contextualInfo.cases') }}
                                    </div>
                                </div>
                                <QCard flat bordered class="bg-grey-2 SimpleContextualInfoMenuCard">
                                    <div class="q-pa-sm row items-center justify-between no-wrap">
                                        <div style="flex: 1; min-width: 0;">
                                            <div class="SimpleContextualInfoMenuValue">
                                                <span class="text-weight-bold">
                                                    {{ dictFormattedData.cases.filteredData }}
                                                </span>
                                                <span>{{ ` ${$t('visualization.filters.contextualInfo.of')} ` }}</span>
                                                <span class="text-weight-bold">
                                                    {{ dictFormattedData.cases.totalData }}
                                                </span>
                                            </div>
                                        </div>
                                        <QCircularProgress
                                            showValue
                                            :value="compactCasesPercentage"
                                            size="44px"
                                            :thickness="0.22"
                                            color="secondary"
                                            trackColor="white"
                                            class="q-ml-sm"
                                        >
                                            <div class="text-caption text-weight-bold text-secondary">
                                                {{ compactCasesPercentage }}%
                                            </div>
                                        </QCircularProgress>
                                    </div>
                                </QCard>
                            </div>

                            <div class="col-3 SimpleContextualInfoMenuCol">
                                <div class="SimpleContextualInfoMenuTitle row items-center q-mb-xs">
                                    <q-icon name="alt_route" color="primary" size="18px" class="q-mr-xs" />
                                    <div class="text-caption text-primary text-weight-bold">
                                        {{ $t('visualization.filters.contextualInfo.variants') }}
                                    </div>
                                </div>
                                <QCard flat bordered class="bg-grey-2 SimpleContextualInfoMenuCard">
                                    <div class="q-pa-sm row items-center justify-between no-wrap">
                                        <div style="flex: 1; min-width: 0;">
                                            <div class="SimpleContextualInfoMenuValue">
                                                <span class="text-weight-bold">
                                                    {{ dictFormattedData.variants.filteredData }}
                                                </span>
                                                <span>{{ ` ${$t('visualization.filters.contextualInfo.of')} ` }}</span>
                                                <span class="text-weight-bold">
                                                    {{ dictFormattedData.variants.totalData }}
                                                </span>
                                            </div>
                                        </div>
                                        <QCircularProgress
                                            showValue
                                            :value="compactVariantsPercentage"
                                            size="44px"
                                            :thickness="0.22"
                                            color="secondary"
                                            trackColor="white"
                                            class="q-ml-sm"
                                        >
                                            <div class="text-caption text-weight-bold text-secondary">
                                                {{ compactVariantsPercentage }}%
                                            </div>
                                        </QCircularProgress>
                                    </div>
                                </QCard>
                            </div>

                            <div class="col-3 SimpleContextualInfoMenuCol">
                                <div class="SimpleContextualInfoMenuTitle row items-center q-mb-xs">
                                    <q-icon name="timer" color="primary" size="18px" class="q-mr-xs" />
                                    <div class="text-caption text-primary text-weight-bold">
                                        {{ $t('visualization.filters.contextualInfo.duration') }}
                                    </div>
                                </div>
                                <QCard flat bordered class="bg-grey-2 SimpleContextualInfoMenuCard">
                                    <div class="q-pa-sm column">
                                        <div class="row items-center no-wrap q-mb-xs">
                                            <q-icon
                                                name="arrow_upward"
                                                color="primary"
                                                size="16px"
                                                class="q-mr-xs"
                                            />
                                            <div class="SimpleContextualInfoMenuValue text-weight-bold">
                                                {{ dictFormattedData.max.filteredData }}
                                            </div>
                                            <QTooltip>
                                                {{ $t('visualization.filters.contextualInfo.max') }}
                                            </QTooltip>
                                        </div>

                                        <div class="row items-center no-wrap q-mb-xs">
                                            <q-icon
                                                name="horizontal_rule"
                                                color="primary"
                                                size="16px"
                                                class="q-mr-xs"
                                            />
                                            <div class="SimpleContextualInfoMenuValue text-weight-bold">
                                                {{ dictFormattedData.avg.filteredData }}
                                            </div>
                                            <QTooltip>
                                                {{ $t('visualization.filters.contextualInfo.avg') }}
                                            </QTooltip>
                                        </div>

                                        <div class="row items-center no-wrap">
                                            <q-icon
                                                name="arrow_downward"
                                                color="primary"
                                                size="16px"
                                                class="q-mr-xs"
                                            />
                                            <div class="SimpleContextualInfoMenuValue text-weight-bold">
                                                {{ dictFormattedData.min.filteredData }}
                                            </div>
                                            <QTooltip>
                                                {{ $t('visualization.filters.contextualInfo.min') }}
                                            </QTooltip>
                                        </div>
                                    </div>
                                </QCard>
                            </div>

                            <div class="col-3 SimpleContextualInfoMenuCol">
                                <div class="SimpleContextualInfoMenuTitle row items-center q-mb-xs">
                                    <q-icon name="event" color="primary" size="18px" class="q-mr-xs" />
                                    <div class="text-caption text-primary text-weight-bold">
                                        {{ $t('visualization.filters.contextualInfo.dateRangeTooltip') }}
                                    </div>
                                </div>
                                <QCard flat bordered class="bg-grey-2 SimpleContextualInfoMenuCard">
                                    <div class="q-pa-sm column items-left no-wrap">
                                        <div class="SimpleContextualInfoMenuValue text-weight-bold" style="min-width: 0; overflow: hidden; text-overflow: ellipsis;">
                                            <q-icon name="sym_o_line_start_square" color="primary" size="14px" class="q-mr-xs" />
                                            {{ dictFormattedData.minStartDateTime.filteredData }}
                                            <QTooltip>
                                                {{ $t('visualization.filters.contextualInfo.minStartDateTime') }}
                                            </QTooltip>
                                        </div>
                                        <div class="SimpleContextualInfoMenuValue text-weight-bold" style="min-width: 0; overflow: hidden; text-overflow: ellipsis;">
                                            <q-icon name="sym_o_line_end_square" color="primary" size="14px" class="q-mr-xs" />
                                            {{ dictFormattedData.maxEndDateTime.filteredData }}
                                            <QTooltip>
                                                {{ $t('visualization.filters.contextualInfo.maxEndDateTime') }}
                                            </QTooltip>
                                        </div>
                                    </div>
                                </QCard>
                            </div>
                        </div>
                    </div>
                </QMenu>
            </QCard>
        </div>
    </div>
</template>

<script>
import VueTypes from 'vue-types'
import moment from 'moment'
import 'moment-duration-format'
import { filtersMixin, filtersStorageMixin } from '@/mixins'
import dashboardChartsMixin from '@/mixins/dashboardCharts.mixin'

export default {
    name: 'ContextualInfo',
    components: {
    },
    mixins: [filtersMixin, filtersStorageMixin, dashboardChartsMixin],
    inject: ['App'],
    props: {
        totalInfo: VueTypes.object.def({}),
        filteredInfo: VueTypes.object.def({}),
    },
    emits: ['onApplyFilter'],
    data() {
        return {
            displayMode: 'filtered',
            datetimeFilters: undefined,
            selectedFilter: null,
            filterDate: {
                start: null,
                end: null,
            },
            compactMenuOpen: false,
            compactMenuCloseTimeoutId: undefined,
        }
    },
    computed: {
        compactCasesPercentage() {
            const percentage = Number.parseFloat(this.dictFormattedData.cases.filteredDataPercentage || 0) * 100
            return Number.isFinite(percentage) ? Math.round(percentage) : 0
        },
        compactVariantsPercentage() {
            const percentage = Number.parseFloat(this.dictFormattedData.variants.filteredDataPercentage || 0) * 100
            return Number.isFinite(percentage) ? Math.round(percentage) : 0
        },
        compactCasesValue() {
            return this.dictFormattedData.cases.filteredData
        },
        compactCasesTotal() {
            return this.dictFormattedData.cases.totalData
        },
        dictFormattedData() {
            const {
                totalInfo, filteredInfo, getDurationFormattedData, getFormattedData,
            } = this
            return {
                cases: getFormattedData('cases'),
                variants: getFormattedData('variants'),
                med: getDurationFormattedData('med'),
                avg: getDurationFormattedData('avg'),
                sd: getDurationFormattedData('sd'),
                min: getDurationFormattedData('min'),
                max: getDurationFormattedData('max'),
                minStartDateTime: {
                    totalData: this.App.dateLocationFormat(totalInfo.minStartDateTime, 'short'),
                    filteredData: this.App.dateLocationFormat(filteredInfo.minStartDateTime, 'short') === '1/1/1970'
                        ? '-'
                        : this.App.dateLocationFormat(filteredInfo.minStartDateTime, 'short'),
                },
                maxEndDateTime: {
                    totalData: this.App.dateLocationFormat(totalInfo.maxEndDateTime, 'short'),
                    filteredData: this.App.dateLocationFormat(filteredInfo.maxEndDateTime, 'short') === '1/1/1970'
                        ? '-'
                        : this.App.dateLocationFormat(filteredInfo.maxEndDateTime, 'short'),
                },
            }
        },
    },
    watch: {
        'dictFormattedData.minStartDateTime.totalData': {
            handler(newValue) {
                if (newValue !== 'No data') {
                    this.filterDate.start = this.formatDateString(this.dictFormattedData.minStartDateTime[`${this.displayMode}Data`])
                }
            },
        },
        'dictFormattedData.maxEndDateTime.totalData': {
            handler(newValue) {
                if (newValue !== 'No data') {
                    this.filterDate.end = this.formatDateString(this.dictFormattedData.maxEndDateTime[`${this.displayMode}Data`])
                }
            },
        },
    },
    mounted() {
        this.filterDate.start = this.formatDateString(this.dictFormattedData.minStartDateTime[`${this.displayMode}Data`])
        this.filterDate.end = this.formatDateString(this.dictFormattedData.maxEndDateTime[`${this.displayMode}Data`])
    },
    methods: {
        toggleCompactMenu() {
            this.compactMenuOpen = !this.compactMenuOpen
        },
        clearCompactMenuCloseTimeout() {
            if (!this.compactMenuCloseTimeoutId) return
            clearTimeout(this.compactMenuCloseTimeoutId)
            this.compactMenuCloseTimeoutId = undefined
        },
        scheduleCompactMenuClose() {
            this.clearCompactMenuCloseTimeout()

            // Allow moving pointer from card to menu.
            this.compactMenuCloseTimeoutId = setTimeout(() => {
                this.compactMenuOpen = false
                this.compactMenuCloseTimeoutId = undefined
            }, 140)
        },
        handleCompactCardMouseEnter() {
            this.clearCompactMenuCloseTimeout()
            this.compactMenuOpen = true
        },
        handleCompactCardMouseLeave() {
            this.scheduleCompactMenuClose()
        },
        handleCompactMenuMouseEnter() {
            this.clearCompactMenuCloseTimeout()
            this.compactMenuOpen = true
        },
        handleCompactMenuMouseLeave() {
            this.scheduleCompactMenuClose()
        },
        getDurationFormattedData(key) {
            const { totalInfo, filteredInfo } = this
            return {
                totalData: totalInfo.duration ? moment.duration(totalInfo.duration[key], 'seconds').format('d[d] h[h]:m[m]:s[s]', { largest: 2, trim: false }) : '-',
                filteredData: filteredInfo.duration ? moment.duration(filteredInfo.duration[key], 'seconds').format('d[d] h[h]:m[m]:s[s]', { largest: 2, trim: false }) : '-',
            }
        },
        getFormattedData(key) {
            const {
                totalInfo, filteredInfo, formatNumber, formatPercentage,
            } = this
            return {
                totalData: this.App.numberLocationFormat(formatNumber(totalInfo[key]), false, true),
                totalDataPercentage: `${formatPercentage(totalInfo[key], totalInfo[key])}`,
                totalDataTooltip: totalInfo[key] !== formatNumber(totalInfo[key]) ? totalInfo[key] : '',
                filteredData: this.App.numberLocationFormat(formatNumber(filteredInfo[key]), false, true),
                filteredDataPercentage: `${formatPercentage(filteredInfo[key], totalInfo[key])}`,
                filteredDataTooltip: filteredInfo[key] !== formatNumber(filteredInfo[key]) ? filteredInfo[key] : '',
            }
        },
        formatPercentage(value, total) {
            return value ? ((value / total)).toFixed(2) : 0
        },
        formatDateString(dateString) {
            const parts = dateString.split('/')
            const day = parts[0]
            const month = parts[1]
            const year = parts[2]
            return `${year}/${month}/${day}`
        },
        capitalizeFirstLetter(string) {
            return string.charAt(0).toUpperCase() + string.slice(1)
        },
        updateInfoTotal(infoTotal) {
            this.infoTotal = infoTotal
        },
    },
}
</script>
<style lang="scss">
    .custom-cursor.q-field--readonly.q-field--float .q-field__native,
    .custom-cursor.q-field--readonly.q-field--float .q-field__input {
        cursor: pointer;
    }

    .Data {
        margin: 2px;

        .q-field--dense .q-field__control,
        .q-field--outlined .q-field__control:after {
            max-height: 30px;
            max-width: 120px;
        }

        .q-field__control-container {
            max-height: 30px;
            max-width: 120px;
        }
    }

    .DataContainer {
        display: flex;
        flex-direction: column;
        height: 100%;
        justify-content: space-around;
    }

    .Duration,
    .Cases,
    .VariantsSidebar {
        width: 110px;
        margin: 10px;

        &.Cases {
            margin-left: 20px;
        }

        &.Duration .q-fab--form-square {
            height: 35px;
        }
    }

    .SimpleContextualInfoCompactLabel {
        min-height: 44px;
    }

    .SimpleContextualInfoCompactCard {
        cursor: pointer;
        overflow: hidden;
    }

    .SimpleContextualInfoCompactLabelLeft {
        padding-right: 10px;
        padding-left: 10px;
    }

    .SimpleContextualInfoCompactLabelRight {
        background-color: $secondary;
        padding-left: 10px;
        padding-right: 10px;
        display: flex;
        align-items: center;
    }

    .SimpleContextualInfoCompactValue {
        font-size: 14px;
        line-height: 1.15;
    }

    .SimpleContextualInfoCompactCaption {
        font-size: 13px;
        line-height: 1.15;
    }

    .SimpleContextualInfoCompactPercentage {
        font-size: 16px;
        line-height: 1;
    }

    .SimpleContextualInfoMenuValue {
        font-size: 15px;
        line-height: 1.2;
    }

    .SimpleContextualInfoMenuCol {
        display: flex;
        flex-direction: column;
    }

    .SimpleContextualInfoMenuCard {
        flex: 1;
        display: flex;
        align-items: center;
    }
</style>
